
import { db, serverTimestamp, doc, setDoc, getDoc, updateDoc, collection } from '../../firebaseClient';
import {
  ApprovalModule,
  ApprovalFlow,
  ApprovalStage,
  ApprovalStageKey,
  ApprovalFlowStatus,
  DeptDecision
} from '../types/approval';
import { logActivity } from './activityLog';

const COLLECTION = 'approvalFlows';

export function buildThreeStageFlow(
  module: ApprovalModule,
  targetId: string,
  createdBy: string,
  createdDept?: string,
  assigneeName?: string,
  coReviewDepts?: string[]
): ApprovalFlow {
  
  const stages: ApprovalStage[] = [];

  // 1. Optional CO_REVIEW Stage (Only for CONTRACT_REVIEW with selected depts)
  if (module === 'CONTRACT_REVIEW' && coReviewDepts && coReviewDepts.length > 0) {
    const initialDecisions: Record<string, DeptDecision> = {};
    coReviewDepts.forEach(deptId => {
      initialDecisions[deptId] = { status: 'PENDING' };
    });

    stages.push({
      key: 'CO_REVIEW',
      label: '多部門會辦',
      approverRole: 'multi-dept',
      status: 'PENDING', // Start immediately
      coReviewDepts: coReviewDepts,
      deptDecisions: initialDecisions
    });
  }

  // 2. Standard Stages
  stages.push(
    {
      key: 'LEGAL_REVIEW',
      label: '法務審查',
      approverRole: 'legal',
      // If there is a Co-Review, Legal is NOT_STARTED, otherwise PENDING
      status: (stages.length > 0) ? 'NOT_STARTED' : 'PENDING',
    },
    {
      key: 'MANAGER_LISA',
      label: '主管核決 (Lisa)',
      approverRole: 'lisa',
      status: 'NOT_STARTED',
    },
    {
      key: 'MANAGER_IRENE',
      label: '主管核決 (Irene)',
      approverRole: 'irene',
      status: 'NOT_STARTED',
    }
  );

  return {
    id: `${module}_${targetId}`,
    module,
    targetId,
    createdBy,
    createdDept,
    assigneeName,
    createdAt: new Date().toISOString(),
    status: 'IN_PROGRESS',
    currentStageKey: stages[0].key, // Start at first stage
    stages,
  };
}

// --- Frontend Todo Sync Helper (Ensures visual updates without backend latency) ---
async function syncTodoFrontend(flow: ApprovalFlow, parentTitle?: string, parentDocCode?: string, currentStatus?: string) {
    // Force consistent ID for Todo
    let todoId = `${flow.module}_${flow.targetId}`; 
    // Handle legacy ID mismatch if needed, but standardizing on Module_TargetID is safest
    if (flow.module === 'SEAL_REQUEST') {
        todoId = `SEAL_REQUEST_${flow.targetId}`;
    }

    // Attempt to fetch parent data if not provided (optimistic)
    if (!parentTitle) {
        // Try known collections
        const colMap: Record<string, string> = {
            'SEAL_REQUEST': 'approval_requests',
            'CONTRACT_REVIEW': 'approval_requests',
            'OFFICIAL_REPLY': 'approval_requests',
            'GOV_PROJECT': 'compliance_governance_projects_v2'
        };
        const colName = colMap[flow.module] || 'approval_requests';
        
        try {
            const snap = await getDoc(doc(db, colName, flow.targetId));
            if (snap.exists) {
                const d = snap.data();
                parentTitle = d?.title || "未命名文件";
                parentDocCode = d?.docCode || d?.contractNo || flow.targetId;
            }
        } catch(e) {/* ignore */}
    }

    const status = currentStatus || flow.status;
    const isClosed = ['APPROVED', 'REJECTED', 'COMPLETED', 'RETURNED', 'FULLY_APPROVED', 'CANCELLED'].includes(status);

    // Standardized Todo Schema
    await setDoc(doc(db, 'todos', todoId), {
        module: flow.module,
        entityId: flow.targetId,
        docNo: parentDocCode || flow.targetId,
        title: parentTitle || `Flow ${flow.targetId}`,
        assigneeName: flow.assigneeName, // Or current stage assignee
        overallStatus: status,
        todoStatus: isClosed ? 'CLOSED' : 'OPEN',
        updatedAt: serverTimestamp(),
        // Just for reference
        syncedBy: 'frontend_v2' 
    }, { merge: true });
}

export async function createOrResetApprovalFlow(
  module: ApprovalModule,
  targetId: string,
  createdBy: string,
  createdDept?: string,
  assigneeName?: string,
  options?: { coReviewDepts?: string[] }
) {
  const flow = buildThreeStageFlow(
    module, 
    targetId, 
    createdBy, 
    createdDept, 
    assigneeName, 
    options?.coReviewDepts
  );
  
  const flowRef = doc(db, COLLECTION, flow.id);
  await setDoc(flowRef, {
    ...flow,
    createdAt: serverTimestamp(),
  });

  // Frontend Sync to ensure Debug page updates immediately
  await syncTodoFrontend(flow).catch(console.error);

  // Map module for consistent Audit Logging (Use 'seal' instead of 'STAMP_REQUEST' or 'SEAL_REQUEST')
  const auditModule = (module === 'SEAL_REQUEST' || module === ('STAMP_REQUEST' as any)) ? 'seal' : module;

  await logActivity({
    module: auditModule as any,
    action: "start_approval_flow",
    targetId: targetId,
    entityId: flow.id,
    entityType: "ApprovalFlow",
    actorId: createdBy,
    actorName: assigneeName || "System",
    details: `Started approval flow for ${module}`
  });

  return flow;
}

export async function getApprovalFlow(flowId: string) {
  const flowRef = doc(db, COLLECTION, flowId);
  const snap = await getDoc(flowRef);
  if (!snap.exists) return null;
  return snap.data() as ApprovalFlow;
}

export async function resetFlowForResubmission(flowId: string) {
  const flowRef = doc(db, COLLECTION, flowId);
  const snap = await getDoc(flowRef);
  if (!snap.exists) return;

  const flow = snap.data() as ApprovalFlow;
  
  const newStages = flow.stages.map(s => {
      // Reset specific fields
      const resetBase = { 
        comment: '', 
        actedAt: undefined, 
        approverName: undefined, 
        approverUserId: undefined,
        edited: false,
        editedAt: undefined,
        editedBy: undefined
      };

      if (s.key === 'CO_REVIEW' && s.deptDecisions) {
         // Reset all department decisions
         const resetDecisions: Record<string, DeptDecision> = {};
         Object.keys(s.deptDecisions).forEach(k => {
             resetDecisions[k] = { status: 'PENDING' };
         });
         return { ...s, ...resetBase, status: 'PENDING', deptDecisions: resetDecisions };
      }

      if (s.key === 'LEGAL_REVIEW') {
          // If Co-Review exists, Legal goes to NOT_STARTED, else PENDING
          const hasCoReview = flow.stages.some(st => st.key === 'CO_REVIEW');
          return { ...s, ...resetBase, status: hasCoReview ? 'NOT_STARTED' : 'PENDING' };
      }
      
      return { ...s, ...resetBase, status: 'NOT_STARTED' };
  });

  const firstStage = newStages[0].key;

  await updateDoc(flowRef, {
      status: 'IN_PROGRESS',
      currentStageKey: firstStage,
      stages: newStages,
      updatedAt: serverTimestamp()
  });

  // Sync Todo
  await syncTodoFrontend({ ...flow, status: 'IN_PROGRESS' }).catch(console.error);

  // Map module for consistency
  const auditModule = (flow.module === 'SEAL_REQUEST' || flow.module === ('STAMP_REQUEST' as any)) ? 'seal' : flow.module;

  await logActivity({
    module: auditModule as any,
    action: "reset_flow",
    entityId: flowId,
    entityType: "ApprovalFlow",
    details: "Flow reset for resubmission"
  });
}

export async function updateStageDecision(params: {
  flowId: string;
  stageKey: ApprovalStageKey;
  decision: 'APPROVED' | 'REJECTED' | 'RETURNED' | 'DONE'; // DONE used for individual co-reviews
  comment: string;
  actorUid: string;
  actorName: string;
  deptCode?: string; // Required for CO_REVIEW
}) {
  const { flowId, stageKey, decision, comment, actorUid, actorName, deptCode } = params;
  const flowRef = doc(db, COLLECTION, flowId);
  const snap = await getDoc(flowRef);
  if (!snap.exists) return;

  const flow = snap.data() as ApprovalFlow;
  let status: ApprovalFlowStatus = flow.status;
  let currentStageKey: ApprovalStageKey | undefined | null = flow.currentStageKey;

  const stages = [...flow.stages];
  const stageIndex = stages.findIndex(s => s.key === stageKey);
  if (stageIndex === -1) return;

  const stage = { ...stages[stageIndex] };
  const nowStr = new Date().toISOString();

  // Check if this is an "edit" (modifying an already completed stage)
  const isEditing = stage.status !== 'PENDING' && stage.status !== 'NOT_STARTED';

  // --- LOGIC FOR CO_REVIEW ---
  if (stage.key === 'CO_REVIEW') {
      if (!deptCode || !stage.deptDecisions || !stage.deptDecisions[deptCode]) {
          console.error("Missing deptCode for Co-Review update");
          return;
      }
      
      const currentDecision = stage.deptDecisions[deptCode];
      const isCoReviewEditing = currentDecision.status === 'DONE';

      // Update the specific department's decision
      stage.deptDecisions[deptCode] = {
          ...currentDecision,
          status: 'DONE',
          comment,
          decidedBy: actorName,
          decidedAt: nowStr,
          actorUid,
          actorName,
          // Add edit tracking if applicable
          edited: isCoReviewEditing,
          editedAt: isCoReviewEditing ? nowStr : undefined,
          editedBy: isCoReviewEditing ? actorName : undefined
      };

      // Check if ALL departments are DONE (only if we aren't already approved)
      // If we are editing, we don't necessarily change stage status unless we want to support un-approving?
      // For now, if stage is already APPROVED, we leave it as APPROVED.
      if (stage.status !== 'APPROVED') {
          const allDone = Object.values(stage.deptDecisions).every(d => d.status === 'DONE');
          if (allDone) {
              stage.status = 'APPROVED'; // Mark stage as approved/done
              // Move to next stage (LEGAL_REVIEW)
              currentStageKey = 'LEGAL_REVIEW';
              const nextIdx = stages.findIndex(s => s.key === 'LEGAL_REVIEW');
              if (nextIdx !== -1) stages[nextIdx].status = 'PENDING';
          }
      }
      
      stages[stageIndex] = stage;
  } 
  // --- LOGIC FOR STANDARD STAGES ---
  else {
      // 1. Update Stage Data
      stage.status = decision as any;
      stage.comment = comment; // Save comment explicitly
      stage.approverUserId = actorUid;
      stage.approverName = actorName;
      stage.actedAt = nowStr;
      
      if (isEditing) {
          stage.edited = true;
          stage.editedAt = nowStr;
          stage.editedBy = actorName;
      }
      
      stages[stageIndex] = stage;

      // 2. Handle Rejection / Return / Approval Logic
      // If we are editing, we might change the flow path (e.g. from Reject to Approve)
      
      if (decision === 'REJECTED' || decision === 'RETURNED') {
        status = 'REJECTED';
        currentStageKey = null; // End flow
      } else {
        // If changing TO Approved (from Pending OR from Rejected/Returned if we allow fixing decision)
        // We need to advance flow IF the next stage is NOT_STARTED.
        
        // If flow was REJECTED and now is APPROVED, revive it to IN_PROGRESS
        if (status === 'REJECTED') {
            status = 'IN_PROGRESS';
        }

        if (stageKey === 'LEGAL_REVIEW') {
            currentStageKey = 'MANAGER_LISA';
            const nextIdx = stages.findIndex(s => s.key === 'MANAGER_LISA');
            if (nextIdx !== -1 && stages[nextIdx].status === 'NOT_STARTED') {
                 stages[nextIdx].status = 'PENDING';
            }
        } else if (stageKey === 'MANAGER_LISA') {
            currentStageKey = 'MANAGER_IRENE';
            const nextIdx = stages.findIndex(s => s.key === 'MANAGER_IRENE');
            if (nextIdx !== -1 && stages[nextIdx].status === 'NOT_STARTED') {
                 stages[nextIdx].status = 'PENDING';
            }
        } else if (stageKey === 'MANAGER_IRENE') {
            status = 'COMPLETED';
            currentStageKey = null;
        }
      }
  }

  // 3. Write updates to Flow Doc
  await updateDoc(flowRef, {
    stages,
    status,
    currentStageKey: currentStageKey === undefined ? null : currentStageKey,
    updatedAt: serverTimestamp()
  });

  // 4. Sync Todo Status
  await syncTodoFrontend({ ...flow, status }, undefined, undefined, status).catch(console.error);

  // 5. [CRITICAL] Write back status to Parent Document (approval_requests)
  // This ensures the "List View" sees the correct status immediately
  if (['SEAL_REQUEST', 'CONTRACT_REVIEW', 'OFFICIAL_REPLY'].includes(flow.module)) {
      try {
          const parentStatus = 
              status === 'COMPLETED' ? 'FULLY_APPROVED' :
              status === 'REJECTED' ? 'REJECTED' :
              decision === 'RETURNED' ? 'RETURNED' :
              'IN_REVIEW'; // Default fallback if still running
          
          await updateDoc(doc(db, 'approval_requests', flow.targetId), {
              status: parentStatus,
              updatedAt: serverTimestamp()
          });
      } catch (e) {
          console.error("Failed to write back status to parent doc", e);
      }
  }

  // Log System Activity - Normalize 'seal'
  const auditModule = (flow.module === 'SEAL_REQUEST' || flow.module === ('STAMP_REQUEST' as any)) ? 'seal' : flow.module;

  await logActivity({
    module: auditModule as any,
    action: isEditing ? `${decision}_EDIT` : decision,
    targetId: flow.targetId,
    entityId: flowId,
    entityType: "ApprovalFlow",
    actorId: actorUid,
    actorName: actorName,
    details: `${stage.label} - ${comment || '(No comment)'} ${isEditing ? '(Edited)' : ''}`
  });
}
